import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { compareAsc, min, startOfDay } from 'date-fns';
import { tap } from 'rxjs/operators';

import { CoachingType } from '../../coaching-base/general';
import {
    createCoachingDetailCommands,
    createCommitmentCommands,
} from '../../coaching-detail/navigate';
import { catchErrorLogRet } from '../../common/rest.operators';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { EventCentreDialogService } from '../../notification/event-centre';
import { ViewGoalCommitment } from '../insight/winning-percentage/core';
import { SortOrder } from '../insight/winning-percentage/grid';
import { CommitmentDetailsDialogComponent } from '../insight/winning-percentage/main/component/commitment-details-dialog/commitment-details-dialog.component';
import { CommitmentDetailsDialogColumnId } from '../insight/winning-percentage/main/model/commitment-details-dialog/commitment-details-dialog-column-id.model';
import { CommitmentDetailsDialogData } from '../insight/winning-percentage/main/model/commitment-details-dialog/commitment-details-dialog-data.model';

import { ActionPickerDialogData } from './action-picker-dialog-data.model';
import { ActionPickerComponent } from './component/action-picker.component';
import { GpLegendDialogComponent } from './component/gp-legend-dialog.component';
import { gpActions } from './const/gp-actions.const';
import { gpPersonas } from './const/gp-personas.const';
import { GpActionType } from './enum/gp-action-type.enum';
import { GpPersonaType } from './enum/gp-persona-type.enum';
import { GpWpStatus } from './enum/gp-wp-status.enum';
import { createActionLanes } from './function/create-action-lanes.function';
import { createPersonaFilterOptions } from './function/create-persona-filter-options.function';
import { createPersonaGroups } from './function/create-persona-groups.function';
import { filterRows } from './function/filter-rows.function';
import { loadGoalCommitments } from './function/load-goal-commitments.function';
import { loadPlaybook } from './function/load-playbook.function';
import { currentMainState, defaultMainState } from './game-plan.state';
import { TranslationKeyMap } from './gp-main.i18n';
import { GpRow } from './gp-row.model';
import { MainState } from './model/main-state.model';
import { ViewActionLane } from './model/view-action-lane.model';
import { ViewPersonaGroup } from './model/view-persona-group.model';
import { ViewGroupedAction } from './view-grouped-action.model';
import { ViewRow } from './view-row.model';

@Component({
    selector: 'cmps-gp-main',
    templateUrl: './gp-main.component.html',
})
@TakeUntilDestroy()
export class GpMainComponent implements OnInit, OnDestroy {
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _state: MainState;

    view: 'person' | 'action' = 'person';
    personSubview: 'tile' | 'grid' = 'tile';

    private _legendDialogRef: CmpsDialogRef<
        GpLegendDialogComponent,
        void
    > | null = null;

    constructor(
        private readonly _dialog: CmpsDialog,
        private readonly _eventCentreDialogService: EventCentreDialogService,
        private readonly _el: ElementRef,
    ) {
        defaultMainState();
        this._state = currentMainState();
    }

    ngOnInit(): void {
        loadPlaybook()
            .pipe(
                tap(() => {
                    this._state.rows = this._state.playbook?.rows?.map((r) =>
                        this._buildViewRow(r),
                    );

                    this._state.totalActionCount = this._state.rows.reduce(
                        (total, r) => {
                            const first = r.groupedAction != null ? 1 : 0;
                            const others = r.otherGroupedActions?.length ?? 0;
                            return total + first + others;
                        },
                        0,
                    );

                    createPersonaGroups();
                    createActionLanes();
                    createPersonaFilterOptions();
                    filterRows();

                    this._measureActionTags();
                }),
                catchErrorLogRet(null),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {}

    private _measureActionTags(): void {
        requestAnimationFrame(() => {
            const host = this._el.nativeElement as HTMLElement;
            const tags: HTMLElement[] = Array.from(
                host.querySelectorAll('.action-tag'),
            );
            if (tags.length === 0) return;

            // Remove min-width, read natural widths, set variable — all in one frame
            host.style.setProperty('--action-tag-width', '0px');
            const maxWidth = Math.ceil(
                Math.max(...tags.map((t) => t.scrollWidth)),
            );
            host.style.setProperty('--action-tag-width', `${maxWidth}px`);
        });
    }

    private _buildViewRow(r: GpRow): ViewRow {
        const a: ViewRow = {
            personaType: null,
            persona: null,
            name: `${r.user?.firstName ?? ''} ${r.user?.lastName ?? ''}`,
            oaIcon: '',
            oaColour: '',
            oaLabel: '',
            wpStatus: null,
            wpIcon: '',
            wpColour: null,
            wpLabel: '',
            wpCanClick: false,
            groupedAction: null,
            otherGroupedActions: [],
            row: r,
        };

        this._deriveOa(r, a);
        this._deriveWp(r, a);
        this._derivePersona(r, a);

        const groupedActions = this._buildGroupedActions(r, a);
        a.groupedAction = groupedActions[0] ?? null;
        a.otherGroupedActions = groupedActions.slice(1);

        return a;
    }

    private _deriveOa(r: GpRow, a: ViewRow): void {
        if (r.oaScore == null) {
            a.oaIcon = 'minus-circle-outline';
            a.oaColour = 'cmps-cdk-fill-grey-300';
            a.oaLabel = TranslationKeyMap._11313_OA_NO_DATA;
        } else {
            if (r.oaScore >= 1) {
                a.oaIcon = 'check-circle-outline';
                a.oaColour = 'cmps-cdk-fill-green-100';
                a.oaLabel = TranslationKeyMap._11314_OA_ON_TARGET;
            } else if (r.oaScore > 0) {
                a.oaIcon = 'alert-circle-outline';
                a.oaColour = 'cmps-cdk-fill-yellow-100';
                a.oaLabel = TranslationKeyMap._11315_OA_BELOW_TARGET;
            } else {
                a.oaIcon = 'alert-circle-outline';
                a.oaColour = 'cmps-cdk-fill-red-100';
                a.oaLabel = TranslationKeyMap._11316_OA_ZERO;
            }
        }
    }

    private _deriveWp(r: GpRow, a: ViewRow): void {
        a.wpStatus = GpWpStatus.UNKNOWN;
        if (r.wpEgcLosingCount > 0) {
            a.wpStatus = GpWpStatus.DECLINE;
        } else if (r.wpEgcNeutralCount > 0) {
            a.wpStatus = GpWpStatus.IMPROVE;
        } else if (r.wpEgcCount > 0) {
            a.wpStatus = GpWpStatus.WIN;
        } else if (r.wpEgcNewCount > 0) {
            a.wpStatus = GpWpStatus.NEW;
        }

        // Colour/icon are owned by the UI (backend no longer sends wpStatusColour).
        switch (a.wpStatus) {
            case GpWpStatus.DECLINE:
                a.wpIcon = 'alert-circle-outline';
                a.wpColour = 'cmps-cdk-fill-red-100';
                a.wpLabel = TranslationKeyMap.__WP_DECLINE;
                a.wpCanClick = true;
                break;
            case GpWpStatus.IMPROVE:
                a.wpIcon = 'alert-box-outline';
                a.wpColour = 'cmps-cdk-fill-grey-400';
                a.wpLabel = TranslationKeyMap.__WP_IMPROVE;
                a.wpCanClick = true;
                break;
            case GpWpStatus.WIN:
                a.wpIcon = 'check-circle-outline';
                a.wpColour = 'cmps-cdk-fill-green-100';
                a.wpLabel = TranslationKeyMap.__WP_WIN;
                a.wpCanClick = true;
                break;
            case GpWpStatus.NEW:
                a.wpIcon = 'clock-outline';
                a.wpColour = 'cmps-cdk-fill-black';
                a.wpLabel = TranslationKeyMap.__WP_NEW;
                a.wpCanClick = true;
                break;
            default:
                a.wpIcon = 'minus-circle-outline';
                a.wpColour = 'cmps-cdk-fill-grey-300';
                a.wpLabel = TranslationKeyMap.__WP_NO_DATA;
                a.wpCanClick = false;
        }
    }

    private _derivePersona(r: GpRow, a: ViewRow): void {
        // OA present and below target (< 1) → needs-coaching, regardless of WP.
        // Otherwise (OA ≥ 1 or OA null) the category is driven by wpStatus:
        //   win → successful-coaching     improve → improving-coaching
        //   new → coaching-ramp           decline → coaching-review
        //   null → on-track
        let personaType: GpPersonaType;
        if (r.oaScore != null && r.oaScore < 1) {
            personaType = GpPersonaType.NEEDS_COACHING;
        } else {
            const wpStatusPersonaMap: { [key in GpWpStatus]: GpPersonaType } = {
                [GpWpStatus.DECLINE]: GpPersonaType.COACHING_REVIEW,
                [GpWpStatus.IMPROVE]: GpPersonaType.IMPROVING_COACHING,
                [GpWpStatus.WIN]: GpPersonaType.SUCCESSFUL_COACHING,
                [GpWpStatus.NEW]: GpPersonaType.COACHING_RAMP,
                [GpWpStatus.UNKNOWN]: GpPersonaType.ON_TRACK,
            };
            personaType = wpStatusPersonaMap[a.wpStatus];
        }

        if (!gpPersonas.has(personaType)) {
            return;
        }

        a.personaType = personaType;
        a.persona = gpPersonas.get(personaType);
    }

    private _buildGroupedActions(
        r: GpRow,
        viewRow: ViewRow,
    ): ViewGroupedAction[] {
        let groupedActions: ViewGroupedAction[] = [];
        gpActions.forEach((value, key) => {
            groupedActions.push({
                actionType: key,
                action: value,
                link: null,
                linkRouterCommands: null,
                button: null,
                count: null,
                buttonClick: null,
                actionPickerItems: null,
                eventCentreCategoryId: null,
                actions: r.actions?.filter((e) => e.type === key),
            });
        });

        groupedActions = groupedActions.filter((e) => e.actions?.length > 0);

        const now = startOfDay(new Date());
        groupedActions.forEach((ga) => {
            if (
                ga.actionType === GpActionType.FOLLOWUP ||
                ga.actionType === GpActionType.UPDATE ||
                ga.actionType === GpActionType.OBSERVE ||
                ga.actionType === GpActionType.APPROVE ||
                ga.actionType === GpActionType.UPDATE_GOAL ||
                ga.actionType === GpActionType.UPDATE_EXPIRED ||
                ga.actionType === GpActionType.CHECK_IN ||
                ga.actionType === GpActionType.CLOSE_OLD ||
                ga.actionType === GpActionType.CLOSE_NEW_MANAGER ||
                ga.actionType === GpActionType.COMPLETE ||
                ga.actionType === GpActionType.RESOLVE
            ) {
                ga.link = ga.actions?.length === 1;
                ga.linkRouterCommands = createCommitmentCommands({
                    userId: r.userId,
                    draftId: ga.actions?.[0]?.draftId,
                    approvalId: ga.actions?.[0]?.approvalId,
                    commitmentId: ga.actions?.[0]?.commitmentId,
                });
                ga.button = ga.actions?.length > 1;
                ga.count = ga.actions?.length;
                ga.actionPickerItems = ga?.actions?.map((e) => ({
                    label: e.behaviourName ?? '',
                    subLabel: e.subBehaviourName ?? null,
                    linkRouterCommands: createCommitmentCommands({
                        userId: r.userId,
                        draftId: e.draftId,
                        approvalId: e.approvalId,
                        commitmentId: e.commitmentId,
                    }),
                    actual: null,
                    target: null,
                    percentToTarget: null,
                    targetColour: null,
                    projectionResult30: null,
                    metrics:
                        e.metrics?.map((m) => ({
                            metricName: m.metricName ?? '',
                            actual: m.actual,
                            goalValue: m.goalValue,
                            percentToGoal: m.percentToGoal,
                            goalColour: m.goalColour,
                            estimatedCompletionDate: m.estimatedCompletionDate,
                            isExpired:
                                startOfDay(m.estimatedCompletionDate) <= now,
                        })) ?? [],
                }));
            }
            if (ga.actionType === GpActionType.COACH) {
                const opps = r.opportunities?.filter(
                    (e) => e.coachTypeId === CoachingType.UNCOACHED,
                );
                ga.link = opps?.length === 1;
                ga.linkRouterCommands = createCoachingDetailCommands({
                    userId: r.userId,
                    metricId: opps?.[0]?.metricId,
                });
                ga.button = opps?.length > 1;
                ga.count = opps?.length;
                ga.actionPickerItems = opps?.map((e) => ({
                    label: e.metricName,
                    subLabel: null,
                    linkRouterCommands: createCoachingDetailCommands({
                        userId: r.userId,
                        metricId: e.metricId,
                    }),
                    actual: e.actual,
                    target: e.target,
                    percentToTarget: e.percentToTarget,
                    targetColour: e.targetColour,
                    projectionResult30: e.projectionResult30,
                    metrics: null,
                }));
            }
            if (ga.actionType === GpActionType.CELEBRATE) {
                ga.link = false;
                ga.button = true;
                ga.count = ga.actions?.length;
                ga.eventCentreCategoryId =
                    ga.actions?.[0]?.eventCentreCategoryId;
            }
            if (
                ga.actionType === GpActionType.REVIEW ||
                ga.actionType === GpActionType.START_DEV_PLAN
            ) {
                ga.link = true;
                ga.linkRouterCommands = [
                    ['/coaching-detail', r.userId],
                    { fromOA: true },
                ];
                ga.count = 1;
            }

            ga.actions.sort((a, b) => {
                const aa = min(
                    a.metrics?.map((e) => e.estimatedCompletionDate) ?? [now],
                );
                const bb = min(
                    b.metrics?.map((e) => e.estimatedCompletionDate) ?? [now],
                );
                return compareAsc(aa, bb);
            });

            ga.buttonClick = () => this._onActionClick(viewRow, ga);
        });

        return groupedActions;
    }

    /** @private */
    _onSearchOpen(): void {
        if (this._state.searchOpen === true) {
            this._state.searchOpen = false;
            this._state.keyword = null;
            filterRows();
        } else {
            this._state.searchOpen = true;
        }
    }

    /** @private */
    _onSearchBlur(): void {
        if (!(this._state.keyword?.length > 0)) {
            this._state.searchOpen = false;
            filterRows();
        }
    }

    /** @private */
    _trackByPersona(_: number, group: ViewPersonaGroup): GpPersonaType {
        return group.personaType;
    }

    /** @private */
    _trackByLane(_: number, lane: ViewActionLane): GpActionType {
        return lane.actionType;
    }

    /** @private */
    _onWpClick(row: ViewRow): void {
        const gcs: ViewGoalCommitment[] = [];

        const data: CommitmentDetailsDialogData = {
            columns: [
                CommitmentDetailsDialogColumnId.DIRECT_REPORT,
                CommitmentDetailsDialogColumnId.START_DATE,
                CommitmentDetailsDialogColumnId.DAYS_ACTIVE,
                CommitmentDetailsDialogColumnId.METRIC,
                CommitmentDetailsDialogColumnId.BEHAVIOUR,
                CommitmentDetailsDialogColumnId.STATE,
                CommitmentDetailsDialogColumnId.CALCULATION_TYPE,
                CommitmentDetailsDialogColumnId.IMPROVEMENT_PERCENTAGE,
                CommitmentDetailsDialogColumnId.WINNING_THRESHOLD,
                CommitmentDetailsDialogColumnId.INITIAL_VALUE,
                CommitmentDetailsDialogColumnId.CURRENT_VALUE,
                CommitmentDetailsDialogColumnId.WINS_AT,
                CommitmentDetailsDialogColumnId.STATUS,
                CommitmentDetailsDialogColumnId.VIEW_COMMITMENT,
            ],
            goalCommitments: gcs,
        };
        const ret = this._dialog.open(CommitmentDetailsDialogComponent, {
            movable: true,
            resizable: true,
            data,
        });

        loadGoalCommitments(row.row.userId ?? 0)
            .pipe(
                tap(() => {
                    (ret._hdl.ref as any).instance._sorter.sortColumn(
                        CommitmentDetailsDialogColumnId.DIRECT_REPORT,
                        this._state.goalCommitments,
                        SortOrder.DESC,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onLegendClick(): void {
        if (this._legendDialogRef != null && !this._legendDialogRef.closed)
            return;
        this._legendDialogRef = this._dialog.open(GpLegendDialogComponent, {
            movable: true,
        });
    }

    /** @private */
    _onActionClick(row: ViewRow, groupedAction: ViewGroupedAction): void {
        if (groupedAction.actionType === GpActionType.CELEBRATE) {
            this._eventCentreDialogService.open(
                groupedAction.eventCentreCategoryId,
                {
                    itemIds: groupedAction.actions.map(
                        (e) => e.eventCentreItemId,
                    ),
                },
            );
            return;
        }

        const data: ActionPickerDialogData = {
            row,
            groupedAction,
        };
        const ref = this._dialog.open(ActionPickerComponent, { data });
        ref.result
            .pipe(takeUntilDestroy(this))
            .subscribe({ complete: () => ref.close() });
    }
}
